import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  CreateFriendshipPayload,
  Friendship,
  FriendProfile,
  FriendshipStatus,
} from "../model/friends";
import { WebApiService } from "./web-api.service";

@Injectable({
  providedIn: "root",
})
export class FriendsService {
  private apiUrl = "Friends";

  constructor(private http: WebApiService) {}

  getFriendshipsForUser(userId: number): Observable<Friendship[]> {
    return this.http
      .get(`${this.apiUrl}/forUser/${userId}`)
      .pipe(map((friendships: Friendship[] | null) => this.normalizeFriendshipList(friendships)));
  }

  createFriendship(payload: CreateFriendshipPayload): Observable<Friendship> {
    return this.http
      .post(this.apiUrl, payload)
      .pipe(
        map((friendship: Friendship) => this.normalizeFriendship(friendship) ?? friendship)
      );
  }

  updateFriendship(payload: Friendship): Observable<Friendship> {
    const sanitizedPayload = this.createUpdatePayload(payload);

    return this.http
      .put(this.apiUrl, sanitizedPayload)
      .pipe(
        map((friendship: Friendship) => this.normalizeFriendship(friendship) ?? friendship)
      );
  }

  private normalizeFriendshipList(friendships: Friendship[] | null): Friendship[] {
    if (!Array.isArray(friendships)) {
      return [];
    }

    return friendships
      .map((friendship) => this.normalizeFriendship(friendship))
      .filter((friendship): friendship is Friendship => !!friendship);
  }

  private normalizeFriendship(friendship: Friendship | null): Friendship | null {
    if (!friendship) {
      return null;
    }

    const userId = this.normalizeId((friendship as any).user_id);
    const friendId = this.normalizeId((friendship as any).friend_id);

    if (userId === null || friendId === null) {
      return null;
    }

    return {
      ...friendship,
      user_id: userId,
      friend_id: friendId,
      status: this.normalizeStatus((friendship as any).status),
      user: this.normalizeProfile(friendship.user, userId),
      friend: this.normalizeProfile(friendship.friend, friendId),
    };
  }

  private normalizeProfile(
    profile: FriendProfile | null | undefined,
    fallbackId: number
  ): FriendProfile | null {
    const normalizedId = this.normalizeId((profile as any)?.id ?? fallbackId);

    if (normalizedId === null) {
      return null;
    }

    return {
      id: normalizedId,
      username: profile?.username ?? undefined,
      name: profile?.name ?? undefined,
      last_name: profile?.last_name ?? undefined,
      email: profile?.email ?? undefined,
      phone_number: profile?.phone_number ?? undefined,
      pp_points: profile?.pp_points ?? undefined,
      profile_image: profile?.profile_image ?? undefined,
    };
  }

  private createUpdatePayload(payload: Friendship): Pick<
    Friendship,
    "user_id" | "friend_id" | "status" | "created_at"
  > {
    return {
      user_id: payload.user_id,
      friend_id: payload.friend_id,
      status: this.normalizeStatus(payload.status),
      created_at: payload.created_at,
    };
  }

  private normalizeStatus(value: unknown): FriendshipStatus {
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === FriendshipStatus.Approved.toLowerCase()) {
        return FriendshipStatus.Approved;
      }
      if (normalized === FriendshipStatus.Pending.toLowerCase()) {
        return FriendshipStatus.Pending;
      }
      if (normalized === FriendshipStatus.Denied.toLowerCase()) {
        return FriendshipStatus.Denied;
      }
    } else if (
      value === FriendshipStatus.Approved ||
      value === FriendshipStatus.Pending ||
      value === FriendshipStatus.Denied
    ) {
      return value;
    }

    return FriendshipStatus.Pending;
  }

  private normalizeId(value: unknown): number | null {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }
}
